import { type UseFormReturn } from 'react-hook-form';
import { useIntl } from 'react-intl';

import { PERMISSION } from 'src/features/auth';
import { type FormSchema } from 'src/features/parkingManagement/components/forms/Zones';
import { parkingManagementMessages } from 'src/features/parkingManagement/utils';
import useHasPermissions from 'src/hooks/useHasPermissions';
import {
    FormFieldBlock,
    FormFieldBlockBody,
    FormFieldBlockLabel,
    FormFieldBlockName,
    FormFieldBlockSeparator,
    FormFieldBlockTitle,
} from 'src/registry/components/blocks/FormFieldBlock';
import { FormControl, FormField, FormItem, FormMessage } from 'src/registry/ui/Form';
import { Input } from 'src/registry/ui/Input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'src/registry/ui/Select';
import { generalMessages } from 'src/utils/messages';

import type { SubParking } from 'src/features/parkingManagement/types/parking';

type ZoneFormFieldsProps = {
    parkingZone: SubParking;
    zoneIndex: number;
    form: UseFormReturn<FormSchema>;
};

const ZoneFormFields = ({ parkingZone, zoneIndex, form }: ZoneFormFieldsProps) => {
    const { formatMessage } = useIntl();
    const zoneUpdatePermissions = useHasPermissions([PERMISSION.PARKING_DETAILS_MANAGE]);

    const LimitCapacity = {
        [formatMessage(generalMessages.yes)]: 'true',
        [formatMessage(generalMessages.no)]: 'false',
    };

    return (
        <>
            <FormFieldBlockTitle className="font-bold">{parkingZone.name}</FormFieldBlockTitle>
            <FormFieldBlockSeparator />
            <FormFieldBlock>
                <FormFieldBlockLabel>
                    <FormFieldBlockTitle>
                        <FormFieldBlockName>{formatMessage(generalMessages.name)}</FormFieldBlockName>
                    </FormFieldBlockTitle>
                </FormFieldBlockLabel>
                <FormFieldBlockBody>
                    <FormField
                        control={form.control}
                        name={`zones.${zoneIndex}.name`}
                        render={({ field }) => (
                            <FormItem className="w-full">
                                <FormControl>
                                    <Input
                                        type="text"
                                        {...field}
                                        value={field.value ?? ''}
                                        disabled={!zoneUpdatePermissions}
                                    />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                </FormFieldBlockBody>
            </FormFieldBlock>

            <FormFieldBlockSeparator />

            <FormFieldBlock>
                <FormFieldBlockLabel>
                    <FormFieldBlockTitle>
                        <FormFieldBlockName>{formatMessage(parkingManagementMessages.capacity)}</FormFieldBlockName>
                    </FormFieldBlockTitle>
                </FormFieldBlockLabel>
                <FormFieldBlockBody>
                    <FormField
                        control={form.control}
                        name={`zones.${zoneIndex}.capacity`}
                        render={({ field }) => (
                            <FormItem className="w-full">
                                <FormControl>
                                    <Input
                                        type="number"
                                        step="1"
                                        {...field}
                                        value={field.value ?? ''}
                                        disabled={!zoneUpdatePermissions}
                                    />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                </FormFieldBlockBody>
            </FormFieldBlock>

            <FormFieldBlockSeparator />

            <FormFieldBlock>
                <FormFieldBlockLabel>
                    <FormFieldBlockTitle>
                        <FormFieldBlockName>
                            {formatMessage(parkingManagementMessages.limitCapacity)}
                        </FormFieldBlockName>
                    </FormFieldBlockTitle>
                </FormFieldBlockLabel>
                <FormFieldBlockBody className="w-full">
                    <FormField
                        control={form.control}
                        name={`zones.${zoneIndex}.limit_to_capacity`}
                        render={({ field }) => {
                            return (
                                <FormItem>
                                    <FormControl>
                                        <Select
                                            onValueChange={(val) => field.onChange(val === 'true')}
                                            value={field.value === undefined ? '' : String(field.value)}
                                            disabled={!zoneUpdatePermissions}
                                        >
                                            <SelectTrigger className="w-full">
                                                <SelectValue
                                                    placeholder={formatMessage(parkingManagementMessages.limitCapacity)}
                                                />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {Object.entries(LimitCapacity).map(([key, value]) => {
                                                    return (
                                                        <SelectItem key={key} value={value}>
                                                            {key}
                                                        </SelectItem>
                                                    );
                                                })}
                                            </SelectContent>
                                        </Select>
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            );
                        }}
                    />
                </FormFieldBlockBody>
            </FormFieldBlock>

            <FormFieldBlockSeparator />
        </>
    );
};

export default ZoneFormFields;
